import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Chip,
  Paper,
  CircularProgress,
  Alert,
  Button,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Checkbox,
  ListItemText,
  Badge,
  Avatar,
  Tooltip,
  Switch,
  ToggleButtonGroup,
  ToggleButton,
  useTheme,
  useMediaQuery,
  TextField,
  InputAdornment,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeMode } from '../../contexts/ThemeModeContext';
import { dataGridHeaderSx } from '../../theme/themes';
import { useNavigate } from 'react-router-dom';
import DevicesIcon from '@mui/icons-material/Devices';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import SmsIcon from '@mui/icons-material/Sms';
import SmsFailedIcon from '@mui/icons-material/SmsFailed';
import RuleIcon from '@mui/icons-material/Rule';
import MapIcon from '@mui/icons-material/Map';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import SearchIcon from '@mui/icons-material/Search';
import DeviceFormDialog from './DeviceFormDialog';
import ChildDevicesModal from './ChildDevicesModal';
import { Device as DeviceModel } from './types';
import { getLatLongDisplay, formatDate } from './utils';
import { useBusiness } from '../../contexts/BusinessContext';

interface BusinessListItem {
  id: string;
  name: string;
  abn: string;
  deviceCount: number;
  isArchived: boolean;
}

const settingValue = (device: DeviceModel, name: string): string | undefined =>
  device.deviceSettings?.find((s) => s.settingName === name && !s.archivedDate)?.settingValue;

const isSettingFlagOff = (value: string | undefined): boolean =>
  value !== undefined && (value.trim() === '0' || value.trim().toLowerCase() === 'false');

type StatusChip = { label: string; color: 'success' | 'warning' | 'default'; tooltip: string };

// A "disable after trigger" rule firing (e.g. a sprung trap) also flips device.sms.enabled
// to 0 and is never reset automatically — see DeviceRulesController.ToggleRule — so this
// single flag is the one place that shows (and controls) whether periodic SMS are suppressed.
const getSmsStatus = (device: DeviceModel): StatusChip => {
  if (isSettingFlagOff(settingValue(device, 'device.sms.enabled'))) {
    return {
      label: 'Disabled',
      color: 'default',
      tooltip: 'device.sms.enabled is set to false — manually re-enable in Device Settings or the SMS tab',
    };
  }
  return { label: 'Enabled', color: 'success', tooltip: 'Periodic detection SMS is enabled' };
};

const getRulesStatus = (device: DeviceModel): StatusChip | null => {
  const rules = (device.rules ?? []).filter((r) => !r.archivedDate);
  if (rules.length === 0) return null;
  const enabledCount = rules.filter((r) => r.isEnabled).length;
  const label = rules.length === 1 && enabledCount === 1 ? 'Enabled' : `${enabledCount}/${rules.length} enabled`;
  const color = enabledCount === rules.length ? 'success' : enabledCount === 0 ? 'default' : 'warning';
  return { label, color, tooltip: `${rules.length} rule${rules.length !== 1 ? 's' : ''} configured, ${enabledCount} enabled` };
};

const DeviceList: React.FC = () => {
  const auth = useAuth();
  const accessToken = auth.tokens?.accessToken;
  const { selectedBusinessId: contextDefaultBusinessId } = useBusiness();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { mode } = useThemeMode();
  const [devices, setDevices] = useState<DeviceModel[]>([]);
  const [businesses, setBusinesses] = useState<BusinessListItem[]>([]);
  const [selectedBusiness, setSelectedBusiness] = useState<string>('all');
  // Types the user has unticked (lower-cased). Tracking exclusions rather than
  // selections means every type — including ones first seen after load or a
  // save — starts ticked, so nothing is hidden by default.
  const [excludedTypes, setExcludedTypes] = useState<string[]>([]);
  const [searchText, setSearchText] = useState('');
  const [showParentOnly, setShowParentOnly] = useState<boolean>(true);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'cards'>('grid');
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState<DeviceModel | null>(null);
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const [viewer, setViewer] = useState<{ open: boolean; src: string | null }>(() => ({ open: false, src: null }));
  const [childModalOpen, setChildModalOpen] = useState(false);
  const [selectedParent, setSelectedParent] = useState<DeviceModel | null>(null);
  const [childCounts, setChildCounts] = useState<Record<string, number>>({});
  const [deleteTarget, setDeleteTarget] = useState<DeviceModel | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Force cards view on mobile
  useEffect(() => {
    if (isMobile && viewMode === 'grid') {
      setViewMode('cards');
    }
  }, [isMobile, viewMode]);

  useEffect(() => {
    if (!accessToken) return;
    fetchBusinesses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  useEffect(() => {
    if (!accessToken) return;
    fetchDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, selectedBusiness, showParentOnly]);

  const fetchBusinesses = async () => {
    try {
      const response = await fetch('/api/businesses/list', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setBusinesses(data);
      }
    } catch (err) {
      console.error('Failed to fetch businesses', err);
    }
  };

  const fetchDevices = async () => {
    try {
      setLoading(true);
      const query = new URLSearchParams();
      query.set('topLevelOnly', showParentOnly ? 'true' : 'false');
      if (selectedBusiness !== 'all') {
        query.set('businessId', selectedBusiness);
      }

      const response = await fetch(`/api/devices?${query.toString()}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (response.ok) {
        const devicesData = await response.json();
        setDevices(devicesData);
        // prefetch thumbnails
        devicesData.forEach((d: DeviceModel) => loadThumb(d.id as string));
        // fetch child counts
        devicesData.forEach((d: DeviceModel) => fetchChildCount(d.id as string));
      } else {
        setError('Failed to fetch devices');
      }
    } catch (err) {
      setError('An error occurred while fetching devices');
    } finally {
      setLoading(false);
    }
  };

  const fetchChildCount = async (deviceId: string) => {
    try {
      const res = await fetch(`/api/devices/${deviceId}/children`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (res.ok) {
        const children = await res.json();
        setChildCounts(prev => ({ ...prev, [deviceId]: children.length }));
      }
    } catch {
      // ignore
    }
  };

  const authHeader = { Authorization: `Bearer ${accessToken}` };
  const loadThumb = async (deviceId?: string) => {
    if (!deviceId || thumbs[deviceId]) return;
    try {
      const res = await fetch(`/api/devices/${deviceId}/latest-image/thumbnail?maxWidth=360`, { headers: authHeader });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setThumbs((p) => ({ ...p, [deviceId]: url }));
    } catch {
      // ignore
    }
  };

  const openLarge = async (deviceId?: string) => {
    if (!deviceId) return;
    try {
      const res = await fetch(`/api/devices/${deviceId}/latest-image/large?maxWidth=1600`, { headers: authHeader });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setViewer({ open: true, src: url });
    } catch {
      // ignore
    }
  };

  const closeViewer = () => {
    if (viewer.src) URL.revokeObjectURL(viewer.src);
    setViewer({ open: false, src: null });
  };

  const openChildModal = (device: DeviceModel, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedParent(device);
    setChildModalOpen(true);
  };

  const isAdmin = auth.hasRole('DMO_ADMIN');

  const openAdd = () => {
    setSelected(null);
    setDialogOpen(true);
  };

  const openEdit = (device: DeviceModel) => {
    setSelected(device);
    setDialogOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const response = await fetch(`/api/devices/${deleteTarget.id}`, {
        method: 'DELETE',
        headers: authHeader,
      });
      if (!response.ok) {
        throw new Error('Failed to delete device');
      }
      setDevices((prev) => prev.filter((d) => d.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      setError('Failed to delete device');
    } finally {
      setDeleting(false);
    }
  };

  const goDetails = (deviceId?: string) => {
    if (!deviceId) return;
    navigate(`/devices/${deviceId}/events`);
  };

  const handleSaved = (saved: DeviceModel) => {
    setDevices((prev) => {
      const idx = prev.findIndex((d) => d.id === saved.id);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], ...saved } as DeviceModel;
        return copy;
      }
      return [saved, ...prev];
    });

    // A newly-added (or retyped) device won't show up if its type is unticked
    // in the Device Type filter — make sure it's always visible right after
    // saving rather than silently disappearing.
    const savedTypeName = saved.deviceType?.name?.toLowerCase();
    if (savedTypeName) {
      setExcludedTypes((prev) => prev.filter((t) => t !== savedTypeName));
    }
  };

  const handleBusinessChange = (event: SelectChangeEvent<string>) => {
    setSelectedBusiness(event.target.value);
  };

  const availableDeviceTypes = Array.from(
    new Map(
      devices
        .map((device) => device.deviceType?.name)
        .filter((name): name is string => Boolean(name))
        .map((name) => [name.toLowerCase(), name])
    ).values()
  ).sort((left, right) => left.localeCompare(right));

  const selectedDeviceTypes = availableDeviceTypes.filter((t) => !excludedTypes.includes(t.toLowerCase()));

  const handleDeviceTypeChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const selected = (typeof value === 'string' ? value.split(',') : value).map((t) => t.toLowerCase());
    setExcludedTypes(
      availableDeviceTypes.map((t) => t.toLowerCase()).filter((t) => !selected.includes(t))
    );
  };

  const search = searchText.trim().toLowerCase();

  const filteredDevices = devices
    .filter((device) => {
      // Devices without a type can't be unticked, so they always show
      const typeName = device.deviceType?.name?.toLowerCase();
      return !typeName || !excludedTypes.includes(typeName);
    })
    .filter((device) =>
      !search
      || (device.name ?? '').toLowerCase().includes(search)
      || (device.deviceType?.name ?? '').toLowerCase().includes(search)
      || (device.id ?? '').toLowerCase().includes(search)
    )
    .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

  // DataGrid columns
  const columns: GridColDef[] = [
    {
      field: 'thumbnail',
      headerName: 'Image',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box
          onClick={(e) => {
            e.stopPropagation();
            openLarge(params.row.id);
          }}
          sx={{ 
            cursor: 'pointer', 
            height: '100%', 
            display: 'flex', 
            alignItems: 'center',
            '&:hover': { opacity: 0.8 }
          }}
        >
          {thumbs[params.row.id] ? (
            <Avatar
              src={thumbs[params.row.id]}
              alt={params.row.name}
              variant="rounded"
              sx={{ width: 60, height: 60 }}
            />
          ) : (
            <Avatar variant="rounded" sx={{ width: 60, height: 60, bgcolor: 'grey.300' }}>
              <DevicesIcon />
            </Avatar>
          )}
        </Box>
      ),
    },
    {
      field: 'name',
      headerName: 'Device Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {params.row.name}
          </Typography>
          {params.row.deviceType && (
            <Chip 
              label={params.row.deviceType.name} 
              size="small" 
              color="primary"
              sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
            />
          )}
        </Box>
      ),
    },
    {
      field: 'location',
      headerName: 'Location',
      width: 150,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const latLong = getLatLongDisplay(params.row.latLong);
        return (
          <Tooltip title={params.row.latLong ? "View on map" : "No location set"}>
            <Chip
              icon={<LocationOnIcon />}
              label={latLong}
              size="small"
              onClick={params.row.latLong ? (e) => {
                e.stopPropagation();
                navigate(`/devices/map?deviceId=${params.row.id}`);
              } : undefined}
              clickable={!!params.row.latLong}
              color={params.row.latLong ? 'primary' : 'default'}
            />
          </Tooltip>
        );
      },
    },
    {
      field: 'children',
      headerName: 'Children',
      width: 120,
      sortable: true,
      valueGetter: (params) => childCounts[params.row.id] || 0,
      renderCell: (params: GridRenderCellParams) => {
        const count = childCounts[params.row.id] || 0;
        return count > 0 ? (
          <Chip
            icon={<AccountTreeIcon />}
            label={`${count} device${count !== 1 ? 's' : ''}`}
            size="small"
            color="secondary"
            onClick={(e) => {
              e.stopPropagation();
              openChildModal(params.row, e);
            }}
          />
        ) : (
          <Typography variant="caption" color="text.secondary">None</Typography>
        );
      },
    },
    {
      field: 'ip',
      headerName: 'IP Address',
      width: 140,
      sortable: false,
      valueGetter: (params) => 
        params.row.deviceSettings?.find((s: any) => s.settingName === 'device.ip')?.settingValue || '',
      renderCell: (params: GridRenderCellParams) => {
        const ip = params.row.deviceSettings?.find((s: any) => s.settingName === 'device.ip')?.settingValue;
        return ip ? (
          <Chip
            icon={<NetworkCheckIcon />}
            label={ip}
            size="small"
            color="success"
            variant="outlined"
          />
        ) : (
          <Typography variant="caption" color="text.secondary">-</Typography>
        );
      },
    },
    {
      field: 'smsStatus',
      headerName: 'SMS',
      width: 170,
      sortable: false,
      filterable: false,
      valueGetter: (params) => getSmsStatus(params.row).label,
      renderCell: (params: GridRenderCellParams) => {
        const status = getSmsStatus(params.row);
        return (
          <Tooltip title={status.tooltip}>
            <Chip
              icon={status.color === 'success' ? <SmsIcon /> : <SmsFailedIcon />}
              label={status.label}
              size="small"
              color={status.color}
              variant={status.color === 'default' ? 'outlined' : 'filled'}
            />
          </Tooltip>
        );
      },
    },
    {
      field: 'rulesStatus',
      headerName: 'Rules',
      width: 150,
      sortable: false,
      filterable: false,
      valueGetter: (params) => getRulesStatus(params.row)?.label || 'None',
      renderCell: (params: GridRenderCellParams) => {
        const status = getRulesStatus(params.row);
        if (!status) {
          return <Typography variant="caption" color="text.secondary">None</Typography>;
        }
        return (
          <Tooltip title={status.tooltip}>
            <Chip
              icon={<RuleIcon />}
              label={status.label}
              size="small"
              color={status.color}
              variant={status.color === 'default' ? 'outlined' : 'filled'}
            />
          </Tooltip>
        );
      },
    },
    {
      field: 'dateCreated',
      headerName: 'Added',
      width: 130,
      type: 'dateTime',
      valueGetter: (params) => new Date(params.row.dateCreated),
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="caption">{formatDate(params.row.dateCreated)}</Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 195,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button 
            size="small" 
            variant="outlined"
            onClick={(e) => { 
              e.stopPropagation(); 
              goDetails(params.row.id); 
            }}
          >
            Details
          </Button>
          {isAdmin && (
            <IconButton
              size="small"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                openEdit(params.row);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
          {isAdmin && (
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteTarget(params.row);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <>
    <Container maxWidth="xl">
      <Box sx={{ mt: 0, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DevicesIcon sx={{ mr: 1 }} />
            <Typography variant="h4" component="h1">
              Device Management
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {!isMobile && (
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newMode) => {
                  if (newMode !== null) {
                    setViewMode(newMode);
                  }
                }}
                size="small"
              >
                <ToggleButton value="grid">
                  <Tooltip title="Grid View">
                    <ViewListIcon />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="cards">
                  <Tooltip title="Card View">
                    <ViewModuleIcon />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            )}
            <Button 
              variant="outlined" 
              startIcon={<MapIcon />} 
              onClick={() => navigate('/devices/map')}
            >
              Map
            </Button>
            {isAdmin && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
                Add Device
              </Button>
            )}
          </Box>
        </Box>

        {businesses.length > 0 && (
          <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl sx={{ minWidth: 250 }}>
              <InputLabel id="business-filter-label">Filter by Business</InputLabel>
              <Select
                labelId="business-filter-label"
                id="business-filter"
                value={selectedBusiness}
                label="Filter by Business"
                onChange={handleBusinessChange}
                startAdornment={<BusinessIcon sx={{ ml: 1, mr: 0.5, color: 'action.active' }} />}
              >
                <MenuItem value="all">
                  <em>All Businesses</em>
                </MenuItem>
                {businesses.map((business) => (
                  <MenuItem key={business.id} value={business.id}>
                    {business.name} ({business.deviceCount} devices)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedBusiness !== 'all' && (
              <Chip 
                label={`Showing: ${businesses.find(b => b.id === selectedBusiness)?.name}`}
                onDelete={() => setSelectedBusiness('all')}
                color="primary"
              />
            )}
            <TextField
              label="Search devices"
              placeholder="Name, type or ID"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{ minWidth: 240 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'action.active' }} />
                  </InputAdornment>
                ),
                endAdornment: searchText ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchText('')} aria-label="Clear search">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
            <FormControl sx={{ minWidth: 280 }}>
              <InputLabel id="device-type-filter-label">Device Type</InputLabel>
              <Select
                labelId="device-type-filter-label"
                id="device-type-filter"
                multiple
                value={selectedDeviceTypes}
                label="Device Type"
                onChange={handleDeviceTypeChange}
                renderValue={(selected) => {
                  const list = selected as string[];
                  if (list.length === availableDeviceTypes.length) return 'All device types';
                  if (list.length === 0) return 'None';
                  return list.join(', ');
                }}
              >
                {availableDeviceTypes.map((deviceType) => (
                  <MenuItem key={deviceType} value={deviceType}>
                    <Checkbox checked={selectedDeviceTypes.includes(deviceType)} />
                    <ListItemText primary={deviceType} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showParentOnly}
                    onChange={(e) => setShowParentOnly(e.target.checked)}
                    color="primary"
                  />
                }
                label="Show parent devices only"
              />
              <Tooltip title={`${filteredDevices.length} visible device${filteredDevices.length !== 1 ? 's' : ''}`}>
                <Badge badgeContent={filteredDevices.length} color="primary">
                  <DevicesIcon color="action" />
                </Badge>
              </Tooltip>
            </Box>
          </Box>
        )}

        {filteredDevices.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <DevicesIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No devices found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {devices.length > 0
                ? 'No devices match the current search and filters'
                : 'Add devices to start monitoring them'}
            </Typography>
          </Paper>
        ) : viewMode === 'grid' ? (
          <Paper sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={filteredDevices}
              columns={columns}
              pageSizeOptions={[10, 25, 50, 100]}
              rowHeight={80}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 25 },
                },
                sorting: {
                  sortModel: [{ field: 'name', sort: 'asc' }],
                },
              }}
              onRowClick={(params: GridRowParams) => goDetails(params.row.id)}
              sx={{
                ...dataGridHeaderSx(mode),
                '& .MuiDataGrid-row': {
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                },
                '& .MuiDataGrid-cell': {
                  display: 'flex',
                  alignItems: 'center',
                  paddingTop: 1,
                  paddingBottom: 1,
                },
              }}
              disableRowSelectionOnClick
            />
          </Paper>
        ) : (

          <Grid container spacing={3}>
            {filteredDevices.map((device) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={device.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', cursor: 'pointer' }} onClick={() => goDetails(device.id)}>
                  {thumbs[device.id as string] ? (
          <CardMedia
                      component="img"
                      sx={{ height: 160, objectFit: 'cover' }}
                      src={thumbs[device.id as string]}
                      alt={device.name}
                      onDoubleClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
            openLarge(device.id);
                      }}
                    />
                  ) : (
                    <CardMedia
                      sx={{
                        height: 160,
                        bgcolor: 'grey.200',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <DevicesIcon sx={{ fontSize: 48, color: 'grey.500' }} />
                    </CardMedia>
                  )}

                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography gutterBottom variant="h6" component="h2">
                      {device.name}
                    </Typography>
                    
                    {device.deviceType && (
                      <Chip 
                        label={device.deviceType.name} 
                        color="primary" 
                        size="small" 
                        sx={{ mb: 1 }}
                      />
                    )}
                    
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        bgcolor: 'grey.100',
                        p: 0.5,
                        borderRadius: 1,
                        mb: 1
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                        ID:
                      </Typography>
                      <Typography 
                        variant="caption"
                        component="code" 
                        sx={{ 
                          fontFamily: 'monospace',
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {device.id}
                      </Typography>
                      <Button 
                        size="small" 
                        variant="text" 
                        sx={{ minWidth: 'auto', p: 0.5 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(device.id || '');
                        }}
                      >
                        Copy
                      </Button>
                    </Box>

                    {device.description && (
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {device.description}
                      </Typography>
                    )}

                    <Box sx={{ mb: 2 }}>
                      <Chip
                        icon={<LocationOnIcon />}
                        label={getLatLongDisplay(device.latLong)}
                        size="small"
                        sx={{ mb: 1, mr: 1 }}
                        onClick={device.latLong ? (e) => {
                          e.stopPropagation();
                          navigate(`/devices/map?deviceId=${device.id}`);
                        } : undefined}
                        clickable={!!device.latLong}
                        color={device.latLong ? 'primary' : 'default'}
                      />
                      <Chip
                        icon={<AccessTimeIcon />}
                        label={`Added: ${formatDate(device.dateCreated)}`}
                        size="small"
                        variant="outlined"
                      />
                      {device.deviceSettings?.find(s => s.settingName === 'device.ip') && (
                        <Chip
                          icon={<NetworkCheckIcon />}
                          label={`IP: ${device.deviceSettings.find(s => s.settingName === 'device.ip')?.settingValue}`}
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ mt: 1 }}
                        />
                      )}
                      {childCounts[device.id as string] > 0 && (
                        <Chip
                          icon={<AccountTreeIcon />}
                          label={`${childCounts[device.id as string]} child device${childCounts[device.id as string] !== 1 ? 's' : ''}`}
                          size="small"
                          color="secondary"
                          sx={{ mt: 1, ml: 1 }}
                          onClick={(e) => openChildModal(device, e)}
                        />
                      )}
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Status: Active
                      </Typography>
                    </Box>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button size="small" onClick={(e) => { e.stopPropagation(); goDetails(device.id); }}>Details</Button>
                      {childCounts[device.id as string] > 0 && (
                        <Button 
                          size="small" 
                          startIcon={<AccountTreeIcon />}
                          onClick={(e) => openChildModal(device, e)}
                          color="secondary"
                        >
                          {childCounts[device.id as string]} Child{childCounts[device.id as string] !== 1 ? 'ren' : ''}
                        </Button>
                      )}
                    </Box>
                    {isAdmin && (
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Button size="small" startIcon={<EditIcon />} onClick={(e) => { e.stopPropagation(); openEdit(device); }}>
                          Edit
                        </Button>
                        <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setDeleteTarget(device); }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
      <DeviceFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={handleSaved}
        initial={selected}
        defaultBusinessId={selectedBusiness !== 'all' ? selectedBusiness : contextDefaultBusinessId}
      />
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Device</DialogTitle>
        <DialogContent>
          <Typography>
            Permanently delete <strong>{deleteTarget?.name}</strong>? This cannot be undone.
          </Typography>
          <Typography sx={{ mt: 1 }} color="text.secondary" variant="body2">
            All of its events and images will be deleted
            {deleteTarget && childCounts[deleteTarget.id as string] > 0
              ? `, along with its ${childCounts[deleteTarget.id as string]} child device${childCounts[deleteTarget.id as string] !== 1 ? 's' : ''} and their images.`
              : '.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDeleteConfirmed} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      {selectedParent && (
        <ChildDevicesModal
          open={childModalOpen}
          onClose={() => setChildModalOpen(false)}
          parentDevice={selectedParent}
        />
      )}
  </Container>
  <Dialog open={viewer.open} onClose={closeViewer} fullWidth maxWidth="md">
      <DialogContent sx={{ position: 'relative', p: 0, bgcolor: 'black' }}>
        <IconButton onClick={closeViewer} sx={{ position: 'absolute', top: 8, right: 8, color: 'white', zIndex: 2 }}>
          <CloseIcon />
        </IconButton>
        {viewer.src ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: { xs: 360, md: 600 } }}>
            <Box component="img" src={viewer.src} alt="full" sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          </Box>
        ) : null}
      </DialogContent>
    </Dialog>
    </>
  );
};

export default DeviceList;
